/**
 * Subscription service for handling subscription status API calls
 */

import API_CONFIG from "../apiConfig";

// Subscription Types enum (matches Python backend)
export enum SubscriptionType {
    PRO = "pro",
    FREE = "free",
    PLUS = "plus",
    ENTERPRISE = "enterprise",

    SUBSCRIPTION_FREE_CODE = 101,
    SUBSCRIPTION_PRO_CODE = 102,
    SUBSCRIPTION_PLUS_CODE = 103,
    SUBSCRIPTION_ENTERPRISE_CODE = 104,
    SUBSCRIPTION_4MONTH_PRO_CODE = 402,
    SUBSCRIPTION_4MONTH_PLUS_CODE = 403,
    SUBSCRIPTION_4MONTH_ENTERPRISE_CODE = 404,
    SUBSCRIPTION_12MONTH_PRO_CODE = 1202,
    SUBSCRIPTION_12MONTH_PLUS_CODE = 1203,
    SUBSCRIPTION_12MONTH_ENTERPRISE_CODE = 1204
}

// General Status Codes enum
export enum StatusCode {
    SUCCESS = 200,
    BAD_REQUEST = 400,
    UNAUTHORIZED = 401,
    FORBIDDEN = 403,
    NOT_FOUND = 404,
    INTERNAL_SERVER_ERROR = 500,
    SERVICE_UNAVAILABLE = 503
}

// Subscription Response interface
export interface SubscriptionResponse {
    endingTimestamp?: string;
    subscriptionId?: string;
    transactionId?: string;
    transactionTimestamp?: string;
    type: string;
    userId: string;
    message: string;
    statusCode: number;
    subscriptionCode: number;
    previous_type?: string;
    previous_subscriptionCode?: number;
}

/**
 * Helper function to check if a plan type is a Pro plan (including new duration-based plans)
 * @param planType The plan type to check
 * @returns boolean indicating if it's a Pro plan
 */
export const isProPlan = (planType: string): boolean => {
    const plan = planType.toLowerCase();
    // Check for legacy "pro" plan or new duration-based pro plans
    return plan === SubscriptionType.PRO ||
        plan.startsWith('pro_') ||
        plan === 'pro_monthly' ||
        plan === 'pro_4months' ||
        plan === 'pro_yearly';
};

/**
 * Helper function to check if a plan type is a Plus plan (including new duration-based plans)
 * @param planType The plan type to check
 * @returns boolean indicating if it's a Plus plan
 */
export const isPlusPlan = (planType: string): boolean => {
    const plan = planType.toLowerCase();
    // Check for legacy "plus" plan or new duration-based plus plans
    return plan === SubscriptionType.PLUS ||
        plan.startsWith('plus_') ||
        plan === 'plus_monthly' ||
        plan === 'plus_4months' ||
        plan === 'plus_yearly';
};

/**
 * Helper function to check if a plan type is an Enterprise plan
 * @param planType The plan type to check
 * @returns boolean indicating if it's an Enterprise plan
 */
export const isEnterprisePlan = (planType: string): boolean => {
    return planType.toLowerCase() === SubscriptionType.ENTERPRISE;
};

/**
 * Get subscription status for a user
 * @param userId The user id to get subscription status for
 * @returns Promise with the subscription status response
 */
export const getSubscriptionStatus = async (userId: string): Promise<SubscriptionResponse> => {
    try {
        const response = await fetch(API_CONFIG.SUBSCRIPTION_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ userId }),
        });

        if (!response.ok) {
            throw new Error(`Error fetching subscription status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Failed to fetch subscription status:', error);
        throw error;
    }
}; 